import { useCallback, useState } from 'react';
import type { DownloadDocumentSubscription } from '@gov-nx/core/events';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { useDocumentDownload } from '@gov-nx/core/hooks';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { DownloadCallback } from '../Download/types';

export const DownloadDocumentListener = () => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Service);
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { download } = useDownloadEvents();

	const [callbacks, setCallbacks] = useState<{
		[souborId: string]: Nullable<
			DownloadCallback<{ documentId: number; souborId: string }>['callback']
		>;
	}>({});

	const { mutate } = useDocumentDownload({
		onSuccess: (data) => {
			download({
				...data,
				id: data.variables.souborId,
				callback: (_id, success, error) => {
					if (success) {
						successMessage(data.fileName);
						onDownload(data.variables, true, null);
					}
					if (error) {
						errorMessage();
						onDownload(data.variables, null, error);
					}
				},
			});
		},
		onError: (error, variables) => {
			errorMessage();
			onDownload(variables, null, error);
		},
	});

	const onDocumentDownload = useCallback(
		(
			_: DownloadDocumentSubscription['type'],
			{
				documentId,
				souborId,
				callback,
			}: DownloadDocumentSubscription['payload']
		) => {
			if (callback) {
				setCallbacks((state) => ({ ...state, [souborId]: callback }));
			}

			setTimeout(() => {
				mutate({ documentId, souborId });
			}, 0);
		},
		[mutate]
	);

	useDownloadEvents({
		onDocumentDownload,
	});

	const onDownload = useCallback(
		(
			variables: { documentId: number; souborId: string },
			success: boolean | null,
			error: Error | null
		) => {
			const callbackFunction = callbacks[variables.souborId];
			if (callbackFunction) {
				callbackFunction(variables, success, error);
				setCallbacks((state) => ({ ...state, [variables.souborId]: null }));
			}
		},
		[callbacks]
	);

	const successMessage = (fileName: string) => {
		toastMessageSuccess(
			t('dokument.stazeni-dokumentu-uspech', {
				fileName,
			})
		);
	};

	const errorMessage = () => {
		toastMessageError(t('dokument.stazeni-dokumentu-chyba'));
	};

	return null;
};
